import { useMutation } from '@provision/graphql-client';
import { useCallback, useMemo, useState } from 'react';
import { RESOLVE_TRIAGE_ACTION_MUTATION } from 'src/graphql/entities/conversation-triage-run/conversation-triage-run.operations';
import { TriageActionResolution } from 'src/graphql/entities/conversation-triage-run/conversation-triage-run.types';
import { useNotifications } from 'src/utils/hooks';

interface ResolveTriageActionResponse {
  resolveTriageAction: {
    id: string;
    status: string;
    visibility: string;
    payload: string;
    appliedAt: string | null;
    appliedBy: string | null;
    applyError: string | null;
  };
}

interface ResolveTriageActionVariables {
  input: {
    conversationTriageRunActionId: string;
    resolution: TriageActionResolution;
  };
}

const TRIAGE_RUNS_QUERY_NAME = 'GetConversationTriageRun';
const CONVERSATION_QUERY_NAME = 'GetConversation';

export interface UseResolveTriageActionResult {
  resolvingIds: ReadonlySet<string>;
  isResolving: (actionId: string) => boolean;
  accept: (actionId: string) => Promise<void>;
  dismiss: (actionId: string) => Promise<void>;
  retry: (actionId: string) => Promise<void>;
}

export function useResolveTriageAction(): UseResolveTriageActionResult {
  const { showErrorAlert } = useNotifications();
  const [resolvingIds, setResolvingIds] = useState<ReadonlySet<string>>(new Set());

  const [resolveTriageActionMutation] = useMutation<
    ResolveTriageActionResponse,
    ResolveTriageActionVariables
  >(RESOLVE_TRIAGE_ACTION_MUTATION, {
    onError: (error) => {
      console.error(error);
      showErrorAlert('Something went wrong updating the triage action, please try again');
    },
  });

  const resolve = useCallback(
    async (actionId: string, resolution: TriageActionResolution) => {
      if (resolvingIds.has(actionId)) {
        return;
      }

      setResolvingIds((current) => new Set(current).add(actionId));

      // Accepting/retrying mutates a conversation property, so refresh the
      // conversation too; dismissing changes nothing on the conversation.
      const refetchQueries =
        resolution === TriageActionResolution.Execute
          ? [TRIAGE_RUNS_QUERY_NAME, CONVERSATION_QUERY_NAME]
          : [TRIAGE_RUNS_QUERY_NAME];

      try {
        await resolveTriageActionMutation({
          variables: { input: { conversationTriageRunActionId: actionId, resolution } },
          refetchQueries,
          awaitRefetchQueries: true,
        });
      } finally {
        setResolvingIds((current) => {
          const next = new Set(current);
          next.delete(actionId);
          return next;
        });
      }
    },
    [resolveTriageActionMutation, resolvingIds],
  );

  const accept = useCallback(
    (actionId: string) => resolve(actionId, TriageActionResolution.Execute),
    [resolve],
  );
  const dismiss = useCallback(
    (actionId: string) => resolve(actionId, TriageActionResolution.Dismiss),
    [resolve],
  );
  const retry = useCallback(
    (actionId: string) => resolve(actionId, TriageActionResolution.Execute),
    [resolve],
  );

  const isResolving = useCallback(
    (actionId: string) => resolvingIds.has(actionId),
    [resolvingIds],
  );

  return useMemo(
    () => ({ resolvingIds, isResolving, accept, dismiss, retry }),
    [resolvingIds, isResolving, accept, dismiss, retry],
  );
}
